import React, { useState } from "react";
import { Download, Trash2, Upload, FileText, Paperclip } from "react-feather";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useUploadLpoAttachmentMutation, useDeleteLpoAttachmentMutation, useLazyGetLpoAttachmentContentQuery } from "../stockApi";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

// Batch199 (2026-09-30): an attachment has a kind. GENERAL is what they all were; INVOICE
// is the supplier's invoice against the LPO (once goods are received); CHEQUE is the payment
// against that invoice (fully received, credit LPOs only - a cash LPO is paid through its
// petty cash voucher). Invoice and cheque carry their own number and date, which the LPO
// list shows in its Invoice and Payment columns. The server enforces the same rules.
export const LPO_ATTACHMENT_KINDS = [
  { value: "GENERAL", label: "General" },
  { value: "INVOICE", label: "Invoice" },
  { value: "CHEQUE", label: "Cheque" },
];
export const lpoAttachmentKindLabel = (k) => LPO_ATTACHMENT_KINDS.find((x) => x.value === k)?.label || "General";
const KIND_BADGE = { INVOICE: "badge-lineinfo", CHEQUE: "badge-linesuccess" };

const invoiceAllowed = (lpo) => ["PARTIALLY_RECEIVED", "FULLY_RECEIVED"].includes(lpo?.status);
const chequeAllowed = (lpo) => lpo?.status === "FULLY_RECEIVED" && lpo?.paymentType === "CREDIT";

// Multiple supporting documents/images per LPO (quotation scans, product
// photos, etc.) - structurally modeled on EmployeeDocuments.jsx (metadata
// list fetched with the parent record, bytes only ever fetched on explicit
// view/download). `canManage` mirrors whoever can create/edit the LPO itself
// (Stock.CreateLpo), same permission the API gates upload/delete on -
// view/download works for anyone who can already see the LPO.
const LpoAttachments = ({ recordGuid, attachments = [], canManage, lpo }) => {
  const [uploadAttachment, { isLoading: isUploading }] = useUploadLpoAttachmentMutation();
  const [deleteAttachment] = useDeleteLpoAttachmentMutation();
  const [fetchContent] = useLazyGetLpoAttachmentContentQuery();
  const [selectedFile, setSelectedFile] = useState(null);
  const [kind, setKind] = useState("GENERAL");
  const [referenceNo, setReferenceNo] = useState("");
  const [referenceDate, setReferenceDate] = useState("");
  const [busyRecordGuid, setBusyRecordGuid] = useState(null);

  const needsReference = kind === "INVOICE" || kind === "CHEQUE";
  const referenceLabel = kind === "CHEQUE" ? "Cheque no." : "Invoice no.";

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Choose a file first");
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error("File must be 5MB or smaller");
      return;
    }
    if (needsReference && !referenceNo.trim()) {
      toast.error(`Enter the ${referenceLabel.toLowerCase()}`);
      return;
    }
    try {
      await uploadAttachment({
        recordGuid,
        file: selectedFile,
        kind,
        referenceNo: needsReference ? referenceNo.trim() : undefined,
        referenceDate: needsReference && referenceDate ? referenceDate : undefined,
      }).unwrap();
      toast.success(kind === "GENERAL" ? "Attachment uploaded" : `${lpoAttachmentKindLabel(kind)} attached`);
      setSelectedFile(null);
      setReferenceNo("");
      setReferenceDate("");
      const input = document.getElementById(`lpo-attach-${recordGuid}`);
      if (input) input.value = "";
    } catch (error) {
      toast.error(error?.message || "Could not upload the attachment");
    }
  };

  const handleView = async (attachment) => {
    setBusyRecordGuid(attachment.recordGuid);
    try {
      const blob = await fetchContent({ recordGuid, attachmentRecordGuid: attachment.recordGuid }).unwrap();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      toast.error(error?.message || "Could not open the attachment");
    } finally {
      setBusyRecordGuid(null);
    }
  };

  const handleDelete = async (attachment) => {
    if (!window.confirm(`Remove "${attachment.originalFileName}"?`)) return;
    try {
      await deleteAttachment({ recordGuid, attachmentRecordGuid: attachment.recordGuid }).unwrap();
      toast.success("Attachment removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove the attachment");
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="mb-3 d-flex align-items-center gap-2"><Paperclip size={16} /> Attachments</h5>

        {canManage && (
          <div className="d-flex gap-2 mb-3 flex-wrap align-items-end">
            <div>
              <label className="form-label small mb-1">Kind</label>
              <select className="form-control" style={{ width: 130 }} value={kind} onChange={(e) => setKind(e.target.value)} disabled={isUploading}>
                {LPO_ATTACHMENT_KINDS.map((k) => (
                  <option
                    key={k.value}
                    value={k.value}
                    disabled={(k.value === "INVOICE" && !invoiceAllowed(lpo)) || (k.value === "CHEQUE" && !chequeAllowed(lpo))}
                  >
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
            {needsReference && (
              <>
                <div>
                  <label className="form-label small mb-1">{referenceLabel} <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" style={{ width: 160 }} maxLength={50} value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} disabled={isUploading} />
                </div>
                <div>
                  <label className="form-label small mb-1">{kind === "CHEQUE" ? "Cheque date" : "Invoice date"}</label>
                  <input type="date" className="form-control" style={{ width: 160 }} value={referenceDate} onChange={(e) => setReferenceDate(e.target.value)} disabled={isUploading} />
                </div>
              </>
            )}
            <div>
              <label className="form-label small mb-1">File</label>
              <input
                id={`lpo-attach-${recordGuid}`}
                type="file"
                className="form-control"
                style={{ maxWidth: 320 }}
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                disabled={isUploading}
              />
            </div>
            <button type="button" className="btn btn-outline-primary" onClick={handleUpload} disabled={isUploading || !selectedFile}>
              <Upload size={14} className="me-1" /> {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        )}
        {canManage && (
          <p className="text-muted small mb-3">
            Invoice can be attached once goods are received against the LPO. Cheque only on a fully received credit LPO
            {lpo?.paymentType === "CASH" ? " - this cash LPO is paid through its petty cash voucher." : "."}
          </p>
        )}

        {attachments.length === 0 && <p className="text-muted small">No attachments yet.</p>}

        {attachments.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Reference</th>
                  <th>File</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {attachments.map((doc) => (
                  <tr key={doc.recordGuid}>
                    <td>
                      {doc.attachmentKind && doc.attachmentKind !== "GENERAL" ? (
                        <span className={`badge ${KIND_BADGE[doc.attachmentKind] || "badge-linesecondary"}`}>{lpoAttachmentKindLabel(doc.attachmentKind)}</span>
                      ) : (
                        <span className="text-muted">General</span>
                      )}
                    </td>
                    <td>
                      {doc.referenceNo || <span className="text-muted">-</span>}
                      {doc.referenceDate && <div className="text-muted small">{dayjs(doc.referenceDate).format("DD MMM YYYY")}</div>}
                    </td>
                    <td><FileText size={14} className="me-1 text-muted" />{doc.originalFileName}</td>
                    <td>{formatBytes(doc.fileSizeBytes)}</td>
                    <td>
                      {doc.uploadedByName || "-"}
                      <div className="text-muted small">{doc.uploadedDate ? dayjs(doc.uploadedDate).format("DD MMM YYYY, HH:mm") : ""}</div>
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-1"
                        onClick={() => handleView(doc)}
                        disabled={busyRecordGuid === doc.recordGuid}
                        title="View / Download"
                      >
                        <Download size={16} />
                      </button>
                      {canManage && (
                        <button type="button" className="btn btn-link btn-sm p-1 text-danger" onClick={() => handleDelete(doc)} title="Remove">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LpoAttachments;
